/**
 * Remote Configuration Service
 * 
 * Fetches configuration from remote proxy endpoint and caches it locally.
 * Provides feature flags, vendor filtering, and kill switch capabilities.
 */

export interface RemoteConfig {
  features: {
    shop?: boolean;
    ads?: boolean;
    calculator?: boolean;
    tracker?: boolean;
    inventory?: boolean;
    library?: boolean;
  };
  vendorsEnabled: string[]; // Array of vendor IDs
  killSwitch: boolean;
  version?: string; // Optional version for cache invalidation
  lastUpdated?: string; // ISO timestamp
}

const DEFAULT_CONFIG: RemoteConfig = {
  features: {
    shop: true,
    ads: true,
    calculator: true,
    tracker: true,
    inventory: true,
    library: true,
  },
  vendorsEnabled: [], // Empty array means all vendors enabled
  killSwitch: false,
};

class RemoteConfigService {
  private config: RemoteConfig = DEFAULT_CONFIG;
  private lastFetchTime: number = 0;
  private fetchPromise: Promise<RemoteConfig> | null = null;
  private readonly CACHE_DURATION = 30 * 60 * 1000; // 30 minutes
  private readonly CONFIG_URL = import.meta.env.VITE_REMOTE_CONFIG_URL || 
    (import.meta.env.VITE_OPENAI_PROXY_URL 
      ? `${import.meta.env.VITE_OPENAI_PROXY_URL}/config`
      : null);

  /**
   * Fetch remote config from proxy endpoint
   */
  async fetchRemoteConfig(): Promise<RemoteConfig> {
    // If no config URL is configured, return defaults
    if (!this.CONFIG_URL) {
      if (import.meta.env.DEV) {
        console.warn('Remote config URL not configured, using defaults');
      }
      return DEFAULT_CONFIG;
    }

    // If there's already a fetch in progress, return that promise
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    this.fetchPromise = (async () => {
      try {
        // Ensure CONFIG_URL is not null (shouldn't happen due to check above, but TypeScript needs this)
        if (!this.CONFIG_URL) {
          return DEFAULT_CONFIG;
        }

        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

        const response = await fetch(this.CONFIG_URL, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`Failed to fetch config: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        
        // Validate and merge with defaults
        const config: RemoteConfig = {
          features: {
            ...DEFAULT_CONFIG.features,
            ...(data.features || {}),
          },
          vendorsEnabled: Array.isArray(data.vendorsEnabled) ? data.vendorsEnabled : DEFAULT_CONFIG.vendorsEnabled,
          killSwitch: typeof data.killSwitch === 'boolean' ? data.killSwitch : DEFAULT_CONFIG.killSwitch,
          version: data.version,
          lastUpdated: data.lastUpdated || new Date().toISOString(),
        };

        // Update cache
        this.config = config;
        this.lastFetchTime = Date.now();

        if (import.meta.env.DEV) {
          console.log('Remote config fetched successfully', {
            killSwitch: config.killSwitch,
            features: config.features,
            vendorsCount: config.vendorsEnabled.length,
          });
        }

        return config;
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('Failed to fetch remote config, using cached/default config:', error);
        }
        // Return cached config if available, otherwise defaults
        return this.config;
      } finally {
        this.fetchPromise = null;
      }
    })();

    return this.fetchPromise;
  }

  /**
   * Get current config (cached or fetch if needed)
   */
  async getRemoteConfig(forceRefresh: boolean = false): Promise<RemoteConfig> {
    const now = Date.now();
    const cacheAge = now - this.lastFetchTime;
    const shouldRefresh = forceRefresh || 
      (cacheAge > this.CACHE_DURATION) ||
      this.lastFetchTime === 0;

    if (shouldRefresh) {
      // Fetch in background, don't block
      this.fetchRemoteConfig().catch((error) => {
        if (import.meta.env.DEV) {
          console.error('Background config refresh failed:', error);
        }
      });
    }

    // Return current cached config immediately
    return this.config;
  }

  /**
   * Get current config synchronously (cached only)
   */
  getConfigSync(): RemoteConfig {
    return this.config;
  }

  /**
   * Check if a feature is enabled
   */
  isFeatureEnabled(feature: keyof RemoteConfig['features']): boolean {
    // If kill switch is active, disable all features
    if (this.config.killSwitch) {
      return false;
    }

    const featureValue = this.config.features[feature];
    // Default to true if not specified
    return featureValue !== false;
  }

  /**
   * Check if a vendor is enabled
   */
  isVendorEnabled(vendorId: string): boolean {
    // If vendorsEnabled is empty, all vendors are enabled
    if (this.config.vendorsEnabled.length === 0) {
      return true;
    }

    return this.config.vendorsEnabled.includes(vendorId);
  }

  /**
   * Check if kill switch is active
   */
  isKillSwitchActive(): boolean {
    return this.config.killSwitch;
  }

  /**
   * Initialize config (call on app start)
   */
  async initialize(): Promise<void> {
    try {
      await this.fetchRemoteConfig();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Failed to initialize remote config:', error);
      }
      // Don't throw - use defaults
    }
  }

  /**
   * Set up periodic refresh
   */
  startPeriodicRefresh(intervalMs: number = 30 * 60 * 1000): () => void {
    const intervalId = setInterval(() => {
      this.fetchRemoteConfig().catch((error) => {
        if (import.meta.env.DEV) {
          console.error('Periodic config refresh failed:', error);
        }
      });
    }, intervalMs);

    // Return cleanup function
    return () => {
      clearInterval(intervalId);
    };
  }
}

// Export singleton instance
export const remoteConfigService = new RemoteConfigService();

